import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { clearBrowserAuthSessionState, hasStoredAuthToken } from '../shared/utils/authPersistence';
import { clearGuestSpectatorSessionState } from '../shared/utils/guestSpectatorSession';

export const AUTH_STATUS = Object.freeze({
  CHECKING: 'checking',
  AUTHENTICATED: 'authenticated',
  GUEST: 'guest',
});

const TOKEN_KEYS = ['token', 'accessToken', 'access_token', 'jwt', 'authToken'];
const SESSION_KEYS = ['npc:auth:session_id'];
const USER_KEYS = ['user', 'user_code', 'userCode'];

function readInitialStatus() {
  return hasStoredAuthToken() ? AUTH_STATUS.CHECKING : AUTH_STATUS.GUEST;
}

/**
 * Removes only authentication/session identity data.
 * Lobby/UI preferences are intentionally kept so guest mode can reuse them.
 */
export function clearStoredAuthForGuest({ clearAutoLogin = true } = {}) {
  // Release the current window lease while the old token/session id are still available.
  // Removing token first would force the server to wait for lease TTL expiration.
  try { clearBrowserAuthSessionState(); } catch (_) {}
  try {
    for (const key of [...TOKEN_KEYS, ...SESSION_KEYS]) localStorage.removeItem(key);
    for (const key of USER_KEYS) localStorage.removeItem(key);
    if (clearAutoLogin) localStorage.removeItem('auto_login');
  } catch (_) {}
}

function clearAuthenticatedRecoveryArtifactsForGuest() {
  // Guest state must never reuse authenticated table/recovery hints.
  try {
    for (const key of [
      'currentTableCode',
      'lastTableCode',
      'tableCode',
      'desktop_multitable_v1',
      'npc:recoveryTables',
      'npc:recovery:tables',
      'npc:recovery:last_ts',
      'npc:closedTableRecoveries:v1',
      'npc:closedTablePendingCancel:v1',
      'npc:tableDisplayCodeMap:v1',
    ]) localStorage.removeItem(key);
  } catch (_) {}
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = String(sessionStorage.key(i) || '');
      if (
        key === 'npc:recoveryTables' ||
        key === 'npc:recovery:last_ts' ||
        key.startsWith('npc:recovery:') ||
        key.startsWith('npc:pendingMatch:') ||
        key.startsWith('npc:joinedTableAck:')
      ) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch (_) {}
}

const AuthStateContext = createContext(null);

export function AuthStateProvider({ children }) {
  const [state, setState] = useState(() => ({
    status: readInitialStatus(),
    user: null,
    reason: '',
  }));

  const setChecking = useCallback((reason = '') => {
    setState((prev) => ({ ...prev, status: AUTH_STATUS.CHECKING, reason: String(reason || '') }));
  }, []);

  const setAuthenticated = useCallback((user = null) => {
    // Guest spectator admissions/table state are ephemeral and must not survive
    // the transition into a real authenticated account.
    try { clearGuestSpectatorSessionState({ reason: 'authenticated' }); } catch (_) {}
    setState({ status: AUTH_STATUS.AUTHENTICATED, user: user || null, reason: '' });
  }, []);

  const setGuest = useCallback((reason = '', options = {}) => {
    if (options?.clearStoredAuth === true) {
      clearStoredAuthForGuest({ clearAutoLogin: options?.clearAutoLogin !== false });
    }
    // A guest shell never owns authenticated recovery/pending-match state.
    // Clear stale hints even when there was no token on initial entry.
    clearAuthenticatedRecoveryArtifactsForGuest();
    setState({ status: AUTH_STATUS.GUEST, user: null, reason: String(reason || '') });
  }, []);

  // 다른 창에서 로그인/로그아웃이 일어난 경우 다음 인증 복구 사이클의 상태만 동기화한다.
  // 실제 서버 검증은 기존 MultiTableRoot의 /profile 복구 로직이 계속 담당한다.
  useEffect(() => {
    const onStorage = (event) => {
      if (event?.key && ![...TOKEN_KEYS, ...SESSION_KEYS].includes(event.key)) return;
      const hasToken = hasStoredAuthToken();
      if (!hasToken) {
        try { clearGuestSpectatorSessionState({ reason: 'storage_token_removed' }); } catch (_) {}
        clearAuthenticatedRecoveryArtifactsForGuest();
      }
      setState((prev) => {
        if (!hasToken) {
          return prev.status === AUTH_STATUS.GUEST
            ? prev
            : { status: AUTH_STATUS.GUEST, user: null, reason: 'storage_token_removed' };
        }
        if (prev.status === AUTH_STATUS.GUEST) {
          return { status: AUTH_STATUS.CHECKING, user: null, reason: 'storage_token_added' };
        }
        return prev;
      });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(() => ({
    ...state,
    isChecking: state.status === AUTH_STATUS.CHECKING,
    isAuthenticated: state.status === AUTH_STATUS.AUTHENTICATED,
    isGuest: state.status === AUTH_STATUS.GUEST,
    setChecking,
    setAuthenticated,
    setGuest,
  }), [state, setChecking, setAuthenticated, setGuest]);

  return <AuthStateContext.Provider value={value}>{children}</AuthStateContext.Provider>;
}

export function useAuthState() {
  const value = useContext(AuthStateContext);
  if (!value) {
    throw new Error('useAuthState must be used inside AuthStateProvider');
  }
  return value;
}
